import { useState, useMemo } from "react";
import type { Recipe } from "@/api/recipes";
import { mockRecipes, generateId } from "@/api/recipes";
import { Button } from "@/components/ui/button";
import { SearchBar } from "@/components/SearchBar";
import { RecipeCard } from "@/components/RecipeCard";
import { RecipeDetail } from "@/components/RecipeDetail";
import { RecipeForm } from "@/components/RecipeForm";
import { EmptyState } from "@/components/EmptyState";
import { DeleteDialog } from "@/components/DeleteDialog";
import "./index.css";

type View =
  | { type: "list" }
  | { type: "detail"; recipeId: string }
  | { type: "form"; recipeId?: string };

export function App() {
  const [recipes, setRecipes] = useState<Recipe[]>(mockRecipes);
  const [view, setView] = useState<View>({ type: "list" });
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Recipe | null>(null);

  const filteredRecipes = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return recipes;
    return recipes.filter((r) => r.title.toLowerCase().includes(q));
  }, [recipes, search]);

  const currentRecipe = useMemo(() => {
    if (view.type === "detail") return recipes.find((r) => r.id === view.recipeId);
    if (view.type === "form" && view.recipeId) return recipes.find((r) => r.id === view.recipeId);
    return undefined;
  }, [view, recipes]);

  const handleSave = (data: { title: string; ingredients: string[]; instructions: string[] }) => {
    if (view.type === "form" && view.recipeId) {
      // Edit existing
      setRecipes((prev) =>
        prev.map((r) =>
          r.id === view.recipeId
            ? { ...r, ...data, updatedAt: new Date().toISOString() }
            : r
        )
      );
      setView({ type: "detail", recipeId: view.recipeId });
    } else {
      // Create new
      const newRecipe: Recipe = {
        id: generateId(),
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setRecipes((prev) => [newRecipe, ...prev]);
      setView({ type: "detail", recipeId: newRecipe.id });
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    setRecipes((prev) => prev.filter((r) => r.id !== deleteTarget.id));
    setDeleteTarget(null);
    setView({ type: "list" });
  };

  return (
    <div className="min-h-screen relative z-10">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* App Header — only on list view */}
        {view.type === "list" && (
          <header className="mb-8 animate-fade-in-up">
            <div className="flex items-end justify-between gap-4 mb-6">
              <div>
                <h1
                  className="text-4xl sm:text-5xl font-black tracking-tight leading-none mb-1"
                  style={{ fontFamily: "var(--font-display)", fontWeight: 900 }}
                >
                  Рецепты
                </h1>
                <p
                  className="text-sm text-muted-foreground"
                  style={{ fontFamily: "var(--font-body)", fontWeight: 300 }}
                >
                  {recipes.length === 0
                    ? "Ваша коллекция пуста"
                    : `${recipes.length} ${pluralize(recipes.length, "рецепт", "рецепта", "рецептов")} в коллекции`}
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => setView({ type: "form" })}
                className="bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5 shrink-0"
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14" />
                  <path d="M12 5v14" />
                </svg>
                <span className="hidden sm:inline">Добавить</span>
              </Button>
            </div>

            {recipes.length > 0 && (
              <SearchBar value={search} onChange={setSearch} />
            )}
          </header>
        )}

        {/* List View */}
        {view.type === "list" && (
          <main>
            {recipes.length === 0 ? (
              <EmptyState isSearch={false} onAdd={() => setView({ type: "form" })} />
            ) : filteredRecipes.length === 0 ? (
              <EmptyState isSearch={true} onAdd={() => setView({ type: "form" })} />
            ) : (
              <div className="grid gap-3">
                {filteredRecipes.map((recipe, i) => (
                  <RecipeCard
                    key={recipe.id}
                    recipe={recipe}
                    index={i}
                    onClick={() => setView({ type: "detail", recipeId: recipe.id })}
                  />
                ))}
              </div>
            )}
          </main>
        )}

        {/* Detail View */}
        {view.type === "detail" && currentRecipe && (
          <RecipeDetail
            recipe={currentRecipe}
            onBack={() => setView({ type: "list" })}
            onEdit={() => setView({ type: "form", recipeId: currentRecipe.id })}
            onDelete={() => setDeleteTarget(currentRecipe)}
          />
        )}

        {/* Form View */}
        {view.type === "form" && (
          <RecipeForm
            recipe={currentRecipe}
            onSave={handleSave}
            onCancel={() =>
              view.recipeId
                ? setView({ type: "detail", recipeId: view.recipeId })
                : setView({ type: "list" })
            }
          />
        )}

        {/* Delete confirmation dialog */}
        {deleteTarget && (
          <DeleteDialog
            recipeName={deleteTarget.title}
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
          />
        )}
      </div>
    </div>
  );
}

function pluralize(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

export default App;
